import { useQuery, useMutation, useTransport, createConnectQueryKey, createQueryOptions } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { useMemo } from 'react'
import type { Transport } from '@connectrpc/connect'

import { listTasks, createTask, updateTask, getTask, getDashboardSummary } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListTasksRequestSchema, UpdateTaskRequestSchema, GetTaskResponseSchema, GetTaskRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import type { ListTasksResponse } from '@/gen/cortado/v1/cortado_pb'
import { organizationStore } from './organization.store'
import { dateToDateOnlyTimestamp } from '@/lib/date-utils'
import { isValidUUID } from '@/lib/uuid'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { TaskStatusEnum, ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { connectTransport } from '@/lib/connect-transport'

export interface ListTasksQueryOptions {
  organizationId: string
  parentType?: ParentTypeEnum
  parentId?: string
  jobId?: string
  assignedTo?: string
  statuses?: TaskStatusEnum[]
  dueDateStart?: Date | Timestamp
  dueDateEnd?: Date | Timestamp
  limit?: number
}

export function createListTasksRequest(options: ListTasksQueryOptions) {
  return create(ListTasksRequestSchema, {
    organizationId: options.organizationId,
    parentType: options.parentType,
    parentId: options.parentId,
    jobId: options.jobId,
    assignedTo: options.assignedTo,
    statuses: options.statuses || [],
    dueDateStart: options.dueDateStart ? (options.dueDateStart instanceof Date ? dateToDateOnlyTimestamp(options.dueDateStart) : options.dueDateStart) : undefined,
    dueDateEnd: options.dueDateEnd ? (options.dueDateEnd instanceof Date ? dateToDateOnlyTimestamp(options.dueDateEnd) : options.dueDateEnd) : undefined,
    limit: options.limit,
  })
}

export function listTasksQueryOptions(
  options: ListTasksQueryOptions,
  transport: Transport = connectTransport,
) {
  return createQueryOptions(listTasks, createListTasksRequest(options), { transport })
}

export function getTaskQueryOptions(taskId: string, transport: Transport = connectTransport) {
  return createQueryOptions(getTask, create(GetTaskRequestSchema, { id: taskId }), { transport })
}

export function useTasksList(options?: {
  parentType?: ParentTypeEnum
  parentId?: string
  jobId?: string
  assignedTo?: string
  statuses?: TaskStatusEnum[]
  dueDateStart?: Date | Timestamp
  dueDateEnd?: Date | Timestamp
  limit?: number
  enabled?: boolean
}) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)

  const request = useMemo(() => {
    return createListTasksRequest({
      organizationId: selectedOrganizationId || '',
      parentType: options?.parentType,
      parentId: options?.parentId,
      jobId: options?.jobId,
      assignedTo: options?.assignedTo,
      statuses: options?.statuses || [],
      dueDateStart: options?.dueDateStart,
      dueDateEnd: options?.dueDateEnd,
      limit: options?.limit,
    })
  }, [selectedOrganizationId, options?.parentType, options?.parentId, options?.jobId, options?.assignedTo, options?.statuses, options?.dueDateStart, options?.dueDateEnd, options?.limit])

  const query = useQuery(
    listTasks,
    request,
    {
      enabled: !!selectedOrganizationId && (options?.enabled ?? true) && (!options?.parentId || isValidUUID(options.parentId)) && (!options?.jobId || isValidUUID(options.jobId)),
    }
  )

  return {
    tasks: query.data?.tasks || [],
    totalCount: query.data?.totalCount || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Hook to create a new task
 *
 * This hook:
 * - Creates a new task for a specified parent (inquiry, contact, booking, or event)
 * - Automatically invalidates list queries after creation
 * - Returns the created task with full details
 *
 * @returns Mutation result with isLoading, isError, and error states
 */
export function useCreateTask() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const mutation = useMutation(
    createTask,
    {
      onSuccess: (response) => {
        const createdTask = response.task

        if (!createdTask) return

        // Invalidate all task list queries (we can't know all possible filter combinations)
        // refetchType: 'all' ensures inactive queries (e.g. calendar) are also refetched
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listTasks,
            transport,
            cardinality: undefined,
          }),
          refetchType: 'all',
        })

        // Invalidate the dashboard aggregate so its task counts/cards refresh too
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getDashboardSummary,
            transport,
            cardinality: undefined,
          }),
          refetchType: 'all',
        })
      },
    }
  )

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to update task data
 *
 * This hook:
 * - Provides mutation function to update task fields
 * - Uses field mask to update only specified fields
 * - Updates cache directly with mutation response
 * - Provides helper function specifically for updating task status
 *
 * @returns Mutation result with updateTaskStatus helper and full mutation access
 */
export function useUpdateTask() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const listTasksKey = createConnectQueryKey({
    schema: listTasks,
    transport,
    cardinality: undefined,
  })

  const mutation = useMutation(
    updateTask,
    {
      onMutate: async (variables) => {
        // Cancel in-flight list queries so they don't overwrite our optimistic update
        await queryClient.cancelQueries({ queryKey: listTasksKey })

        // Snapshot all listTasks caches for rollback
        const previousLists = queryClient.getQueriesData<ListTasksResponse>({
          queryKey: listTasksKey,
        })

        // Optimistically update the task in all cached lists
        if (variables.updateMask?.paths?.includes('task_status')) {
          for (const [key, data] of previousLists) {
            if (!data?.tasks) continue
            queryClient.setQueryData(key, {
              ...data,
              tasks: data.tasks.map((task) =>
                task.id === variables.id
                  ? { ...task, taskStatus: variables.taskStatus }
                  : task
              ),
            })
          }
        }

        return { previousLists }
      },

      onError: (_error, _variables, context) => {
        // Rollback all list caches to their pre-mutation state
        if (context?.previousLists) {
          for (const [key, data] of context.previousLists) {
            queryClient.setQueryData(key, data)
          }
        }
      },

      onSuccess: (response) => {
        const updatedTask = response.task
        if (!updatedTask) return

        // Update individual task cache (getTask) with server response
        const queryKey = createConnectQueryKey({
          schema: getTask,
          transport,
          input: { id: updatedTask.id },
          cardinality: 'finite',
        })
        queryClient.setQueryData(
          queryKey,
          create(GetTaskResponseSchema, { task: updatedTask })
        )
      },

      onSettled: () => {
        // Refetch lists in background for eventual consistency
        queryClient.invalidateQueries({ queryKey: listTasksKey })

        // Refetch the dashboard aggregate so its task counts/cards stay in sync
        queryClient.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getDashboardSummary,
            transport,
            cardinality: undefined,
          }),
        })
      },
    }
  )

  const updateTaskStatus = async (taskId: string, newStatus: TaskStatusEnum) => {
    return mutation.mutateAsync(
      create(UpdateTaskRequestSchema, {
        id: taskId,
        taskStatus: newStatus,
        updateMask: create(FieldMaskSchema, {
          paths: ['task_status'],
        }),
      })
    )
  }

  return {
    mutation,
    updateTaskStatus,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Hook to prefetch a task on hover
 *
 * This hook provides a prefetch function that can be called on hover
 * to load task data into the cache before the user clicks.
 * Skips prefetching if the data is already in the cache.
 *
 * @returns Prefetch function
 */
export function usePrefetchTask() {
  const queryClient = useQueryClient()
  const transport = useTransport()

  const prefetchTask = async (taskId: string) => {
    const queryKey = createConnectQueryKey({
      schema: getTask,
      transport,
      input: { id: taskId },
      cardinality: 'finite',
    })

    // Skip prefetch if data is already in cache
    const cachedData = queryClient.getQueryData(queryKey)
    if (cachedData) {
      return
    }

    await queryClient.prefetchQuery(
      getTaskQueryOptions(taskId, transport)
    )
  }

  return { prefetchTask }
}
